// New cohort-aware calculations for NewAnalytics

// Build a Map of employeeId -> most recent response object from the selected cohort
const buildCurrentByEmployee = (selectedResponses) => {
  const currentByEmployee = new Map();
  selectedResponses
    .map((i) => i.responses)
    .flat()
    .forEach((r) => {
      const employeeId = r?.employee_id ?? r?.employee;
      if (employeeId != null && r?.response && !Array.isArray(r.response)) {
        currentByEmployee.set(employeeId, { ...r.response, employee: employeeId });
      }
    });
  return currentByEmployee;
};

// Collect last responses for each requested employeeId from non-selected responses
// "last" means responses from surveys that came BEFORE (chronologically earlier than) the selected one(s)
export const get_last_responses2 = (sortedResponses, selectedResponseIds) => {
  // sortedResponses is full list (desc by id), selectedResponseIds are the chosen record ids
  const selectedSet = new Set(selectedResponseIds || []);

  // Identify requested cohort from selected responses
  const selectedResponses = sortedResponses.filter((r) => selectedSet.has(r.id));
  if (selectedResponses.length === 0) return [];

  // Get the minimum ID (oldest) from selected responses - only look at data before this
  const oldestSelectedId = Math.min(...selectedResponses.map((r) => r.id));

  const requestedSet = new Set();
  selectedResponses.forEach((sr) => {
    (sr?.request_emp_id || []).forEach((eid) => requestedSet.add(eid));
  });

  // Build set of completed employees in selected to skip pulling their own latest from selected
  const completedSelected = new Set();
  selectedResponses.forEach((sr) => {
    (sr?.completed_emp_id || []).forEach((eid) => completedSelected.add(eid));
  });

  // Build map so we don't duplicate employees
  const lastByEmployee = new Map();

  // Iterate other responses (not selected) in descending id order to capture last occurrence first
  // Only consider responses that are older than the oldest selected one
  for (const respGroup of sortedResponses) {
    if (selectedSet.has(respGroup.id)) continue; // only other surveys
    if (respGroup.id >= oldestSelectedId) continue; // only surveys that came before the selected ones

    const list = respGroup?.responses || [];
    for (const r of list) {
      const empId = r?.employee_id ?? r?.employee;
      if (empId == null) continue;
      if (!requestedSet.has(empId)) continue; // only employees requested this time
      if (lastByEmployee.has(empId)) continue; // already captured most recent prior
      const value = r?.response;
      if (value && !Array.isArray(value)) {
        lastByEmployee.set(empId, { ...value, employee: empId });
      }
    }
  }

  // If some requested employees never appeared previously, fall back to current (if present),
  // to ensure the caller downstream still has aligned headcount. This maintains cohort alignment.
  const currentByEmployee = buildCurrentByEmployee(selectedResponses);
  requestedSet.forEach((empId) => {
    if (!lastByEmployee.has(empId) && currentByEmployee.has(empId)) {
      lastByEmployee.set(empId, currentByEmployee.get(empId));
    }
  });

  return Array.from(lastByEmployee.values());
};

// Cohort-aware historical aggregation: only include responses whose employee_id is in the requested cohort
export const sort_historical_data2 = (responsesAsc, requestedEmployeeIds) => {
  // responsesAsc must be ascending by date (oldest -> newest), like the original call site after reverse()
  const requested = new Set(requestedEmployeeIds || []);
  if (!responsesAsc || responsesAsc.length === 0) return [];

  let _responses = [];
  let start = new Date(new Date(responsesAsc[0].date).setDate(0));
  let copied_start = new Date(start.getTime());
  let end = new Date(responsesAsc.slice(-1)[0].date);
  let current = new Date(copied_start.getFullYear(), copied_start.getMonth() + 2, 1);

  while (start < end) {
    const filteredGroups = responsesAsc.filter((f) => {
      const r_date = new Date(f.date);
      return r_date > start && r_date < current;
    });

    if (filteredGroups.length > 0) {
      const flattened = filteredGroups
        .map((i) => i.responses)
        .flat()
        .filter((r) => {
          const empId = r?.employee_id ?? r?.employee;
          return empId != null && requested.has(empId);
        })
        .map((r) => r.response)
        .filter((v) => v && !Array.isArray(v));

      if (flattened.length > 0) {
        _responses.push({ date: start, responses: flattened });
      }
    }

    start = new Date(current.getTime());
    current = new Date(current.getFullYear(), current.getMonth() + 2, 0);
  }

  return _responses;
};

// GROUP METHOD: choose the latest single non-selected survey as "last"
// "last" means the most recent survey that came BEFORE (chronologically earlier than) the selected one(s)
export const get_last_responses_group = (allSortedDesc, selectedResponseIds) => {
  const selectedSet = new Set(selectedResponseIds || []);
  
  // Find the oldest selected response to determine what "before" means
  const selectedResponses = allSortedDesc.filter((r) => selectedSet.has(r.id));
  if (selectedResponses.length === 0) return [];
  
  // Get the minimum ID (oldest) from selected responses
  const oldestSelectedId = Math.min(...selectedResponses.map((r) => r.id));
  
  // Find the first non-selected response that is older than the oldest selected one
  const latestNonSelected = allSortedDesc.find((r) => !selectedSet.has(r.id) && r.id < oldestSelectedId);
  if (!latestNonSelected) return [];
  
  const list = Array.isArray(latestNonSelected.responses) ? latestNonSelected.responses : [];
  return list
    .map((r) => r && r.response && !Array.isArray(r.response) ? { ...r.response, employee: r?.employee_id ?? r?.employee } : null)
    .filter(Boolean);
};

// GROUP METHOD historical: a simple sequence of prior single-survey "last" snapshots
// Return array of { date, responses } in chronological order based on survey dates
// Only includes surveys that came BEFORE (chronologically earlier than) the selected one(s)
export const sort_historical_data_group = (allResponsesDesc, selectedResponseIds) => {
  const selectedSet = new Set(selectedResponseIds || []);
  
  // Find the oldest selected response to determine the cutoff
  const selectedResponses = (allResponsesDesc || []).filter((r) => selectedSet.has(r.id));
  const oldestSelectedId = selectedResponses.length > 0 
    ? Math.min(...selectedResponses.map((r) => r.id))
    : Infinity;
  
  // Take all non-selected surveys that are older than the oldest selected one
  const nonSelected = (allResponsesDesc || []).filter((r) => !selectedSet.has(r.id) && r.id < oldestSelectedId);
  const chronological = [...nonSelected].reverse();
  
  return chronological.map((grp) => {
    const list = Array.isArray(grp.responses) ? grp.responses : [];
    const flattened = list
      .map((r) => r && r.response && !Array.isArray(r.response) ? { ...r.response, employee: r?.employee_id ?? r?.employee } : null)
      .filter(Boolean);
    return { date: grp.date, responses: flattened };
  });
};


